import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { getCachedTemplates } from '@/lib/stores/templatesStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getStatusColor } from '@/lib/utils/statusColors';
import { useNavigate } from 'react-router-dom';
import { FieldValueRenderer } from './FieldValueRenderer';
import CardBase from '../cards/CardBase';

interface ObjectNodeData {
  itemId: string;
  itemType: 'object';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const ObjectNodeExpanded = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as ObjectNodeData;
  
  // Get the actual object data
  const objects = selectedGame?.id ? getCachedObjects(selectedGame.id) : [];
  const object = objects.find(obj => obj.id === nodeData.itemId);
  
  // Get blueprint for color
  const templates = selectedGame?.id ? getCachedTemplates(selectedGame.id) : [];
  const template = object?.blueprint_id ? templates.find(tp => tp.id === object.blueprint_id) : null;
  
  // Get status color
  const statusColor = object?.status ? getStatusColor(object.status, 'objects', selectedGame?.id) : undefined;
  
  // Determine background color (blueprint color or default)
  const bgColor = template?.settings?.color || '#6366f1';
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (object?.id) {
      navigate(`/library/${object.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[200px] max-w-[300px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t"
        style={{ backgroundColor: bgColor as string }}
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-cube" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Collapse"
        >
          <IconProvider iconstring="fa-compress" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Expanded Body with detailed information */}
      <div className="px-3 py-3 space-y-2">
        {/* Basic Info Section */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Basic Info
          </div>
          
          {object?.type && (
            <div className="flex items-center gap-2 text-xs">
              <IconProvider iconstring="fa-tag" size={10} className="text-muted-foreground" />
              <span className="font-medium">Type:</span>
              <span className="text-muted-foreground">{object.type}</span>
            </div>
          )}
          
          {object?.status && (
            <div className="flex items-center gap-2 text-xs">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: statusColor }}
              />
              <span className="font-medium">Status:</span>
              <span style={{ color: statusColor }}>{object.status}</span>
            </div>
          )}
          
          {template && (
            <div className="flex items-center gap-2 text-xs">
              <IconProvider iconstring="fa-file-lines" size={10} className="text-muted-foreground" />
              <span className="font-medium">Template:</span>
              <span className="text-muted-foreground">{template.name}</span>
            </div>
          )}
        </div>
        
        {/* Tags Section */}
        {object?.tags && object.tags.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Tags
            </div>
            <div className="flex flex-wrap gap-1">
              {object.tags.slice(0, 5).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-0.5 text-xs rounded bg-secondary text-secondary-foreground"
                >
                  {tag}
                </span>
              ))}
              {object.tags.length > 5 && (
                <span className="px-2 py-0.5 text-xs text-muted-foreground">
                  +{object.tags.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Description Section */}
        {object.description && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Description
            </div>
            <div className="text-xs text-muted-foreground line-clamp-3">
              {object.description}
            </div>
          </div>
        )}
        
        {/* Blueprint Fields Section */}
        {object?.data && Array.isArray(object.data) && object.data.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Fields
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {object.data.map((section: any, sectionIndex: number) => (
                <div key={sectionIndex} className="space-y-0.5">
                  {/* Section name if available */}
                  {section.name && section.fields?.length > 0 && (
                    <div className="text-xs font-medium text-foreground mt-1">
                      {section.name}
                    </div>
                  )}
                  
                  {/* Render fields */}
                  {Array.isArray(section.fields) &&
                    section.fields.slice(0, 6).map((field: any, fieldIndex: number) => (
                      <FieldValueRenderer
                        key={fieldIndex}
                        fieldLabel={field.label || field.id}
                        fieldValue={field.value}
                        fieldType={field.type || 'text'}
                      />
                    ))}
                  
                  {/* Show remaining fields count */}
                  {Array.isArray(section.fields) && section.fields.length > 6 && (
                    <div className="text-xs text-muted-foreground italic pl-2">
                      +{section.fields.length - 6} more fields in this section
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CardBase>
  );
});

ObjectNodeExpanded.displayName = 'ObjectNodeExpanded';

